import { z } from 'zod';

import { dashboardT } from '../../../lib/messages';
import {
  PlacementDeliveryType,
  PlacementIvtIntegrationType,
  PlacementIvtType,
  PlacementPricingType,
  PlacementStatus,
  PlacementType,
  PlacementXpParameter,
  type PlacementDemandIntegration,
  type PlacementTargeting,
} from './model';

const numericString = z.string().refine(isNonnegativeNumber, {
  message: dashboardT('placements.validation.nonnegative'),
});
const percentageString = z.string().refine(isPercentage, {
  message: dashboardT('placements.validation.percentage'),
});
const optionalIvtType = z.union([z.enum(PlacementIvtType), z.literal('')]);
const optionalIvtIntegrationType = z.union([z.enum(PlacementIvtIntegrationType), z.literal('')]);

export const placementFormValueSchema = z.object({
  type: z.enum(PlacementType),
  status: z.enum(PlacementStatus),
  name: z.string().trim().min(1, dashboardT('placements.validation.required')),
  publisherId: z.string().min(1, dashboardT('placements.validation.required')),
  deliveryType: z.enum(PlacementDeliveryType),
  position: z.string(),
  bidFloor: numericString,
  qpsLimit: numericString,
  maxAdDuration: numericString,
  shareOfVoice: percentageString,
  notes: z.string(),
  debug: z.boolean(),
  ignoreCompliance: z.boolean(),
  passToDemandAppData: z.boolean(),
  passToDemandAppStoreIds: z.boolean(),
  blockBidsWithoutCatOrAdomain: z.boolean(),
  ivtEnabled: z.boolean(),
  ivtType: optionalIvtType,
  ivtIntegrationType: optionalIvtIntegrationType,
  ivtSamplingPercentage: percentageString,
  ivtGeneralRejectPercentage: percentageString,
  ivtSophisticatedRejectPercentage: percentageString,
  impressionTrackers: z.array(
    z.object({
      tracker: z.string().trim().min(1, dashboardT('placements.validation.required')),
      sampleRate: percentageString,
    }),
  ),
  upstreamCpmCapEnabled: z.boolean(),
  upstreamCpmCapMinCpm: numericString,
  upstreamCpmCapMaxCpm: numericString,
  rewriteVastEnabled: z.boolean(),
  rewriteVastVersion: z.string(),
  schainIgnoreRequest: z.boolean(),
  schainPreserveFirstOnly: z.boolean(),
  poddingEnabled: z.boolean(),
  maxPodSlotCount: numericString,
  podFloor: numericString,
  minPodDuration: numericString,
  maxPodDuration: numericString,
  deduplicateUniqueMediaFile: z.boolean(),
  deduplicateUniqueAdvertiserDomain: z.boolean(),
  deduplicateUniqueIABCategory: z.boolean(),
  pricingType: z.enum(PlacementPricingType),
  pricing: numericString,
  lineItemMargin: percentageString,
  demandIntegrations: z.array(z.custom<PlacementDemandIntegration>(isObjectValue)),
  targetings: z.array(z.custom<PlacementTargeting>(isObjectValue)),
  exclusionsEnabled: z.boolean(),
  advertiserDomainsText: z.string(),
  excludedCreativeIdsText: z.string(),
  excludedIabCategoryIds: z.array(z.string()),
  bcAvEnabled: z.boolean(),
  bcVideoEnabled: z.boolean(),
  bcAlwaysUseCache: z.boolean(),
  stitcherEnabled: z.boolean(),
  stAcceptPercentageOfFloor: percentageString,
  stMaxBids: numericString,
  stMaxAvBids: numericString,
  stMaxVideoBids: numericString,
  stMaxBidsFromCache: numericString,
  stIgnoreAdDuration: z.boolean(),
  stPoddingFloor: numericString,
  stFeeCpm: numericString,
  stDedupeEnabled: z.boolean(),
  stDedupeUniqueMediaFile: z.boolean(),
  stDedupeUniqueAdvertiserDomain: z.boolean(),
  stDedupeUniqueIABCategory: z.boolean(),
  dmEnabled: z.boolean(),
  dmShortTimeoutMs: numericString,
  dmLongTimeoutMs: numericString,
  adUnit: z.object({
    name: z.string(),
    widgetName: z.string(),
    publisherDomainIds: z.array(z.string()),
    video: z.boolean(),
    adWidth: numericString,
    adHeight: numericString,
    devicePlacement: z.string(),
    s2s: z.boolean(),
    bidder: z.boolean(),
    viewIn: z.string(),
    viewOut: z.string(),
    adLoadDelay: numericString,
    closeButton: z.boolean(),
    adhesion: z.boolean(),
    noFillHideUnit: z.boolean(),
    idSystemIdentityLink: z.boolean(),
    ibv: z.boolean(),
    refreshRate: numericString,
    maxRefresh: numericString,
    weMakeSticky: z.boolean(),
  }),
  overridePublisherXpParametersMapping: z.boolean(),
  xpParametersMappings: z
    .array(
      z
        .object({
          value: z.string(),
          xpParameter: z.union([z.enum(PlacementXpParameter), z.literal('')]),
        })
        .superRefine((row, context) => {
          if (Boolean(row.value.trim()) === Boolean(row.xpParameter)) return;
          context.addIssue({
            code: 'custom',
            path: row.value.trim() ? ['xpParameter'] : ['value'],
            message: dashboardT('placements.validation.mappingPair'),
          });
        }),
    )
    .max(10),
});

export type PlacementFormValue = z.infer<typeof placementFormValueSchema>;

export function createPlacementFormSchema(options: { validateAdUnit?: boolean } = {}) {
  return placementFormValueSchema.superRefine((value, context) => {
    if (value.pricingType !== PlacementPricingType.None && !value.pricing.trim()) {
      addIssue(context, ['pricing'], 'placements.validation.required');
    }
    if (value.ivtEnabled) {
      if (!value.ivtType) addIssue(context, ['ivtType'], 'placements.validation.required');
      if (!value.ivtIntegrationType) {
        addIssue(context, ['ivtIntegrationType'], 'placements.validation.required');
      }
    }
    validateRange(context, value.upstreamCpmCapMinCpm, value.upstreamCpmCapMaxCpm, [
      'upstreamCpmCapMaxCpm',
    ]);
    validateRange(context, value.minPodDuration, value.maxPodDuration, ['maxPodDuration']);
    validateNumberBounds(
      context,
      value.maxAdDuration,
      ['maxAdDuration'],
      0,
      5787,
      false,
      'placements.validation.maxAdDurationRange',
    );
    if (value.stitcherEnabled) {
      validateNumberBounds(context, value.stMaxBids, ['stMaxBids'], 2, 5, true);
      validateNumberBounds(context, value.stMaxAvBids, ['stMaxAvBids'], 0, 5);
      validateNumberBounds(context, value.stMaxVideoBids, ['stMaxVideoBids'], 0, 5, true);
      validateNumberBounds(context, value.stMaxBidsFromCache, ['stMaxBidsFromCache'], 0, 5);
    }
    if (value.dmEnabled && !value.dmShortTimeoutMs) {
      addIssue(context, ['dmShortTimeoutMs'], 'placements.validation.timeoutRequired');
    }
    if (value.schainIgnoreRequest && value.schainPreserveFirstOnly) {
      addIssue(context, ['schainPreserveFirstOnly'], 'placements.validation.schainExclusive');
    }
    if (value.type === PlacementType.AdUnit && options.validateAdUnit !== false) {
      if (!value.adUnit.widgetName.trim()) {
        addIssue(context, ['adUnit', 'widgetName'], 'placements.validation.required');
      }
      if (value.adUnit.publisherDomainIds.length === 0) {
        addIssue(context, ['adUnit', 'publisherDomainIds'], 'placements.validation.required');
      }
      if (!value.adUnit.adWidth || !value.adUnit.adHeight) {
        addIssue(context, ['adUnit', 'adWidth'], 'placements.validation.dimensions');
      }
    }
  });
}

function validateNumberBounds(
  context: z.RefinementCtx,
  input: string,
  path: PropertyKey[],
  minimum: number,
  maximum: number,
  required = false,
  messageKey: Parameters<typeof dashboardT>[0] = 'placements.validation.stitcherRange',
) {
  if (!input.trim()) {
    if (required) addIssue(context, path, 'placements.validation.required');
    return;
  }
  const value = Number(input);
  if (!Number.isFinite(value) || value < minimum || value > maximum) {
    addIssue(context, path, messageKey);
  }
}

export function emptyPlacementFormValue(type: PlacementType): PlacementFormValue {
  return {
    type,
    status: PlacementStatus.Active,
    name: '',
    publisherId: '',
    deliveryType:
      type === PlacementType.AdUnit ? PlacementDeliveryType.Web : PlacementDeliveryType.Ctv,
    position: '',
    bidFloor: '',
    qpsLimit: '',
    maxAdDuration: '',
    shareOfVoice: '',
    notes: '',
    debug: false,
    ignoreCompliance: false,
    passToDemandAppData: false,
    passToDemandAppStoreIds: false,
    blockBidsWithoutCatOrAdomain: false,
    ivtEnabled: false,
    ivtType: '',
    ivtIntegrationType: '',
    ivtSamplingPercentage: '',
    ivtGeneralRejectPercentage: '',
    ivtSophisticatedRejectPercentage: '',
    impressionTrackers: [],
    upstreamCpmCapEnabled: false,
    upstreamCpmCapMinCpm: '',
    upstreamCpmCapMaxCpm: '',
    rewriteVastEnabled: false,
    rewriteVastVersion: '4',
    schainIgnoreRequest: false,
    schainPreserveFirstOnly: false,
    poddingEnabled: false,
    maxPodSlotCount: '',
    podFloor: '',
    minPodDuration: '',
    maxPodDuration: '',
    deduplicateUniqueMediaFile: false,
    deduplicateUniqueAdvertiserDomain: false,
    deduplicateUniqueIABCategory: false,
    pricingType: PlacementPricingType.None,
    pricing: '',
    lineItemMargin: '',
    demandIntegrations: [],
    targetings: [],
    exclusionsEnabled: false,
    advertiserDomainsText: '',
    excludedCreativeIdsText: '',
    excludedIabCategoryIds: [],
    bcAvEnabled: false,
    bcVideoEnabled: false,
    bcAlwaysUseCache: false,
    stitcherEnabled: false,
    stAcceptPercentageOfFloor: '',
    stMaxBids: '2',
    stMaxAvBids: '0',
    stMaxVideoBids: '1',
    stMaxBidsFromCache: '0',
    stIgnoreAdDuration: false,
    stPoddingFloor: '',
    stFeeCpm: '',
    stDedupeEnabled: false,
    stDedupeUniqueMediaFile: false,
    stDedupeUniqueAdvertiserDomain: false,
    stDedupeUniqueIABCategory: false,
    dmEnabled: false,
    dmShortTimeoutMs: '',
    dmLongTimeoutMs: '',
    adUnit: {
      name: '',
      widgetName: '',
      publisherDomainIds: [],
      video: false,
      adWidth: '',
      adHeight: '',
      devicePlacement: '',
      s2s: false,
      bidder: false,
      viewIn: '',
      viewOut: '',
      adLoadDelay: '',
      closeButton: false,
      adhesion: false,
      noFillHideUnit: false,
      idSystemIdentityLink: false,
      ibv: false,
      refreshRate: '',
      maxRefresh: '',
      weMakeSticky: false,
    },
    overridePublisherXpParametersMapping: false,
    xpParametersMappings: [],
  };
}

function isNonnegativeNumber(value: string) {
  if (!value.trim()) return true;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0;
}

function isObjectValue(value: unknown) {
  return typeof value === 'object' && value !== null;
}

function isPercentage(value: string) {
  if (!value.trim()) return true;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 && parsed <= 100;
}

function validateRange(context: z.RefinementCtx, minimum: string, maximum: string, path: string[]) {
  if (!minimum || !maximum || Number(minimum) <= Number(maximum)) return;
  addIssue(context, path, 'placements.validation.range');
}

function addIssue(
  context: z.RefinementCtx,
  path: PropertyKey[],
  key: Parameters<typeof dashboardT>[0],
) {
  context.addIssue({ code: 'custom', path, message: dashboardT(key) });
}
